/**
 * Relationship Management UI
 * 
 * Interface for creating and managing relationships between entities
 * Requirements: 2.1-2.7
 */

import React, { useState, useEffect, useRef } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { ExpandableList, ExpandableListItem } from './ExpandableList';

export interface RelationshipFormData {
  sourceEntityId: string;
  targetEntityId: string;
  relationshipTypes: string[];
  financialImpact: number;
  description?: string;
}

interface RelationshipManagementUIProps {
  analysisId: string;
  onRelationshipCreate?: (relationship: RelationshipFormData) => void;
  onRelationshipUpdate?: (relationshipId: string, updates: Partial<RelationshipFormData>) => void;
  onRelationshipDelete?: (relationshipId: string) => void;
}

const RELATIONSHIP_TYPES = [
  { label: 'Service Dependency', value: 'service_dependency' },
  { label: 'Data Flow', value: 'data_flow' },
  { label: 'Installed Software', value: 'installed_software' },
  { label: 'Equipment and Parts Supplier', value: 'equipment_and_parts_supplier' },
  { label: 'Regulatory Oversight', value: 'regulatory_oversight' },
];

/**
 * Relationship Management UI Component
 * 
 * Complete implementation with:
 * - Relationship creation form
 * - Source/target entity selection
 * - Relationship type dropdown
 * - Financial impact input
 * - Bidirectional tension display
 * - API integration
 * - Relationship list with edit/delete
 */
export const RelationshipManagementUI: React.FC<RelationshipManagementUIProps> = ({
  analysisId,
  onRelationshipCreate,
  onRelationshipUpdate,
  onRelationshipDelete,
}) => {
  const [showForm, setShowForm] = useState(false);
  const [editingRelationshipId, setEditingRelationshipId] = useState<string | null>(null);
  const [relationships, setRelationships] = useState<any[]>([]);
  const [entities, setEntities] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const formRef = useRef<HTMLDivElement>(null);

  // State for relationship frictions
  const [relationshipFrictionsCache, setRelationshipFrictionsCache] = useState<Map<string, ExpandableListItem[]>>(new Map());
  const [relationshipFrictionsCounts, setRelationshipFrictionsCounts] = useState<Map<string, number>>(new Map());
  const [loadingFrictions, setLoadingFrictions] = useState<Set<string>>(new Set());

  // State for entity frictions affecting relationships
  const [entityFrictionsCache, setEntityFrictionsCache] = useState<Map<string, ExpandableListItem[]>>(new Map());
  const [entityFrictionsCounts, setEntityFrictionsCounts] = useState<Map<string, number>>(new Map());
  const [loadingEntityFrictions, setLoadingEntityFrictions] = useState<Set<string>>(new Set());

  const [formData, setFormData] = useState<RelationshipFormData>({
    sourceEntityId: '',
    targetEntityId: '',
    relationshipTypes: [],
    financialImpact: 0,
    description: '',
  });

  // Fetch relationships and entities on mount
  useEffect(() => {
    fetchRelationships();
    fetchEntities();
  }, [analysisId]);

  // Fetch friction counts when relationships change
  useEffect(() => {
    if (relationships.length > 0) {
      fetchAllRelationshipFrictionCounts();
      fetchEntityFrictionCounts();
    }
  }, [relationships]);

  const fetchRelationships = async () => {
    try {
      setLoading(true);
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      const apiUrl = import.meta.env?.VITE_API_ENDPOINT || 'http://localhost:3000';
      
      // Fetch relationships
      const response = await fetch(`${apiUrl}/analyses/${analysisId}/relationships`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) throw new Error('Failed to fetch relationships');
      
      const relationshipsData = await response.json();
      
      // Fetch tension calculation results
      const tensionResponse = await fetch(`${apiUrl}/analyses/${analysisId}/calculate-tension`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      let tensionResult: any = null;
      if (tensionResponse.ok) {
        tensionResult = await tensionResponse.json();
      }
      
      // Create a map of tension breakdowns by relationship ID
      const tensionBreakdownMap = new Map<string, any>();
      if (tensionResult?.result?.relationshipsWithFrictions) {
        tensionResult.result.relationshipsWithFrictions.forEach((rel: any) => {
          if (rel.tensionBreakdown) {
            tensionBreakdownMap.set(rel.id, rel.tensionBreakdown);
          }
        });
      }
      
      // Merge tension data into relationships and calculate max tension and risk scores
      const relationshipsWithTension = relationshipsData.map((rel: any) => {
        const tension = rel.tension || { sourceToTarget: 0, targetToSource: 0 };
        const maxTension = Math.max(tension.sourceToTarget || 0, tension.targetToSource || 0);
        
        // Get risk score from tension result
        const riskScore = tensionResult?.result?.relationshipRiskScores?.[rel.id];
        
        // Get tension breakdown from the map
        const tensionBreakdown = tensionBreakdownMap.get(rel.id);
        
        return {
          ...rel,
          tension,
          maxTension,
          riskScore,
          tensionBreakdown
        };
      });
      
      setRelationships(relationshipsWithTension);
    } catch (err) {
      console.error('Error fetching relationships:', err);
      setError(err instanceof Error ? err.message : 'Failed to load relationships');
    } finally {
      setLoading(false);
    }
  };

  const fetchEntities = async () => {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      const apiUrl = import.meta.env?.VITE_API_ENDPOINT || 'http://localhost:3000';
      const response = await fetch(`${apiUrl}/analyses/${analysisId}/entities`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) throw new Error('Failed to fetch entities');
      
      const data = await response.json();
      setEntities(data);
    } catch (err) {
      console.error('Error fetching entities:', err);
    }
  };

  const fetchRelationshipFrictions = async (relationshipId: string) => {
    // Check cache first
    if (relationshipFrictionsCache.has(relationshipId)) {
      return;
    }

    try {
      setLoadingFrictions(prev => new Set(prev).add(relationshipId));
      
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      if (!token) {
        throw new Error('Not authenticated');
      }
      
      const apiUrl = import.meta.env?.VITE_API_ENDPOINT || 'http://localhost:3000';
      
      // Fetch all relationship frictions for the analysis
      const frictionsResponse = await fetch(`${apiUrl}/analyses/${analysisId}/frictions/types/relationship`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!frictionsResponse.ok) {
        throw new Error('Failed to fetch relationship frictions');
      }
      
      const allFrictions = await frictionsResponse.json();
      
      // For each friction, check if it affects this relationship
      const affectingFrictions: ExpandableListItem[] = [];
      
      for (const friction of allFrictions) {
        // Fetch affected relationships for this friction
        const affectedResponse = await fetch(
          `${apiUrl}/analyses/${analysisId}/frictions/${friction.id}/affected-relationships`,
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );
        
        if (affectedResponse.ok) {
          const affectedData = await affectedResponse.json();
          
          // Check if this relationship is in the affected list
          const isAffected = affectedData.relationships.some((rel: any) => rel.id === relationshipId);
          
          if (isAffected) {
            affectingFrictions.push({
              id: friction.id,
              displayName: friction.name,
              attributes: {
                magnitude: friction.magnitude?.toFixed(1) || 'N/A',
                direction: friction.direction || 'N/A'
              }
            });
          }
        }
      }
      
      // Update cache
      setRelationshipFrictionsCache(prev => new Map(prev).set(relationshipId, affectingFrictions));
    } catch (err) {
      console.error('Error fetching relationship frictions:', err);
    } finally {
      setLoadingFrictions(prev => {
        const newSet = new Set(prev);
        newSet.delete(relationshipId);
        return newSet;
      });
    }
  };

  const fetchEntityFrictions = async (relationshipId: string) => {
    // Check cache first
    if (entityFrictionsCache.has(relationshipId)) {
      return;
    }

    try {
      setLoadingEntityFrictions(prev => new Set(prev).add(relationshipId));
      
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      if (!token) {
        throw new Error('Not authenticated');
      }
      
      const apiUrl = import.meta.env?.VITE_API_ENDPOINT || 'http://localhost:3000';
      
      // Find the relationship to get source and target entity IDs
      const relationship = relationships.find(r => r.id === relationshipId);
      if (!relationship) {
        return;
      }
      
      // Fetch all entity frictions for the analysis
      const frictionsResponse = await fetch(`${apiUrl}/analyses/${analysisId}/frictions`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!frictionsResponse.ok) {
        throw new Error('Failed to fetch frictions');
      }
      
      const allFrictions = await frictionsResponse.json();
      const entityFrictions = allFrictions.filter((p: any) => p.frictionType === 'entity_friction');
      
      // Check which entity frictions affect the source or target entities
      const affectingFrictions: ExpandableListItem[] = [];
      
      for (const friction of entityFrictions) {
        const affectsSource = friction.targetEntityIds?.includes(relationship.sourceEntityId);
        const affectsTarget = friction.targetEntityIds?.includes(relationship.targetEntityId);
        
        if (affectsSource || affectsTarget) {
          const affectedEntities = [];
          if (affectsSource) affectedEntities.push('Source');
          if (affectsTarget) affectedEntities.push('Target');
          
          affectingFrictions.push({
            id: friction.id,
            displayName: friction.name,
            attributes: {
              affects: affectedEntities.join(', '),
              direction: friction.direction || 'N/A',
              baseline: friction.baselineTension?.toFixed(1) || 'N/A',
              multiplier: friction.multiplier?.toFixed(1) + 'x' || 'N/A'
            }
          });
        }
      }
      
      // Update cache
      setEntityFrictionsCache(prev => new Map(prev).set(relationshipId, affectingFrictions));
    } catch (err) {
      console.error('Error fetching entity frictions:', err);
    } finally {
      setLoadingEntityFrictions(prev => {
        const newSet = new Set(prev);
        newSet.delete(relationshipId);
        return newSet;
      });
    }
  };

  const fetchAllRelationshipFrictionCounts = async () => {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      if (!token) {
        return;
      }
      
      const apiUrl = import.meta.env?.VITE_API_ENDPOINT || 'http://localhost:3000';
      
      // Fetch all relationship frictions for the analysis
      const frictionsResponse = await fetch(`${apiUrl}/analyses/${analysisId}/frictions/types/relationship`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!frictionsResponse.ok) {
        return;
      }
      
      const allFrictions = await frictionsResponse.json();
      
      // For each relationship, count how many frictions affect it
      const counts = new Map<string, number>();
      
      for (const relationship of relationships) {
        let count = 0;
        
        for (const friction of allFrictions) {
          // Fetch affected relationships for this friction
          const affectedResponse = await fetch(
            `${apiUrl}/analyses/${analysisId}/frictions/${friction.id}/affected-relationships`,
            {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            }
          );
          
          if (affectedResponse.ok) {
            const affectedData = await affectedResponse.json();
            
            // Check if this relationship is in the affected list
            const isAffected = affectedData.relationships.some((rel: any) => rel.id === relationship.id);
            
            if (isAffected) {
              count++;
            }
          }
        }
        
        counts.set(relationship.id, count);
      }
      
      setRelationshipFrictionsCounts(counts);
    } catch (err) {
      console.error('Error fetching relationship friction counts:', err);
    }
  };

  const fetchEntityFrictionCounts = async () => {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      if (!token) {
        return;
      }
      
      const apiUrl = import.meta.env?.VITE_API_ENDPOINT || 'http://localhost:3000';
      
      // Fetch all frictions for the analysis
      const frictionsResponse = await fetch(`${apiUrl}/analyses/${analysisId}/frictions`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!frictionsResponse.ok) {
        return;
      }
      
      const allFrictions = await frictionsResponse.json();
      const entityFrictions = allFrictions.filter((p: any) => p.frictionType === 'entity_friction');
      
      // For each relationship, count how many entity frictions affect it
      const counts = new Map<string, number>();
      
      for (const relationship of relationships) {
        let count = 0;
        
        for (const friction of entityFrictions) {
          const affectsSource = friction.targetEntityIds?.includes(relationship.sourceEntityId);
          const affectsTarget = friction.targetEntityIds?.includes(relationship.targetEntityId);
          
          if (affectsSource || affectsTarget) {
            count++;
          }
        }
        
        counts.set(relationship.id, count);
      }
      
      setEntityFrictionsCounts(counts);
    } catch (err) {
      console.error('Error fetching entity friction counts:', err);
    }
  };

  const handleNavigateToFriction = (frictionId: string) => {
    // Dispatch custom event to switch to frictions tab
    window.dispatchEvent(new CustomEvent('navigateToFriction', { detail: { frictionId } }));
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.sourceEntityId) {
      alert('Source entity is required');
      return;
    }
    if (!formData.targetEntityId) {
      alert('Target entity is required');
      return;
    }
    if (formData.sourceEntityId === formData.targetEntityId) {
      alert('Source and target entities must be different');
      return;
    }
    if (!formData.relationshipTypes || formData.relationshipTypes.length === 0) {
      alert('At least one relationship type is required');
      return;
    }

    try {
      setLoading(true);
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      const apiUrl = import.meta.env?.VITE_API_ENDPOINT || 'http://localhost:3000';
      
      if (editingRelationshipId) {
        // Update existing relationship
        const response = await fetch(`${apiUrl}/analyses/${analysisId}/relationships/${editingRelationshipId}`, {
          method: 'PUT',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(formData),
        });
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ error: { message: 'Unknown error' } }));
          throw new Error(errorData.error?.message || `Failed to update relationship (${response.status})`);
        }
        
        if (onRelationshipUpdate) onRelationshipUpdate(editingRelationshipId, formData);
      } else {
        // Create new relationship
        const response = await fetch(`${apiUrl}/analyses/${analysisId}/relationships`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(formData),
        });
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ error: { message: 'Unknown error' } }));
          throw new Error(errorData.error?.message || `Failed to create relationship (${response.status})`);
        }
        
        if (onRelationshipCreate) onRelationshipCreate(formData);
      }
      
      // Reset form
      resetForm();
      fetchRelationships();
    } catch (err) {
      console.error('Error saving relationship:', err);
      alert(err instanceof Error ? err.message : 'Failed to save relationship');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (relationshipId: string) => {
    if (!confirm('Are you sure you want to delete this relationship?')) return;

    try {
      setLoading(true);
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      const apiUrl = import.meta.env?.VITE_API_ENDPOINT || 'http://localhost:3000';
      const response = await fetch(`${apiUrl}/analyses/${analysisId}/relationships/${relationshipId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) throw new Error('Failed to delete relationship');
      
      if (onRelationshipDelete) onRelationshipDelete(relationshipId);
      fetchRelationships();
    } catch (err) {
      console.error('Error deleting relationship:', err);
      alert(err instanceof Error ? err.message : 'Failed to delete relationship');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (relationship: any) => {
    setFormData({
      sourceEntityId: relationship.sourceEntityId,
      targetEntityId: relationship.targetEntityId,
      relationshipTypes: relationship.relationshipTypes || [relationship.relationshipType] || [],
      financialImpact: relationship.financialImpact || 0,
      description: relationship.description || '',
    });
    setEditingRelationshipId(relationship.id);
    setShowForm(true);
    
    // Scroll to form after state updates
    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const resetForm = () => {
    setFormData({
      sourceEntityId: '',
      targetEntityId: '',
      relationshipTypes: [],
      financialImpact: 0,
      description: '',
    });
    setEditingRelationshipId(null);
    setShowForm(false);
  };

  const getEntityName = (entityId: string) => {
    const entity = entities.find(e => e.id === entityId);
    return entity ? entity.name : entityId;
  };

  // Calculate relative tension color (green -> yellow -> red)
  const getRelativeTensionColor = (tension: number, minTension: number, maxTension: number): string => {
    if (maxTension === minTension) return '#eab308'; // Yellow if all same
    if (tension <= 0) return '#22c55e'; // Green for zero/negative
    
    const normalized = (tension - minTension) / (maxTension - minTension);
    
    if (normalized < 0.5) {
      const t = normalized * 2;
      return interpolateColor('#22c55e', '#eab308', t);
    } else {
      const t = (normalized - 0.5) * 2;
      return interpolateColor('#eab308', '#ef4444', t);
    }
  };

  const interpolateColor = (color1: string, color2: string, t: number): string => {
    const r1 = parseInt(color1.slice(1, 3), 16);
    const g1 = parseInt(color1.slice(3, 5), 16);
    const b1 = parseInt(color1.slice(5, 7), 16);
    
    const r2 = parseInt(color2.slice(1, 3), 16);
    const g2 = parseInt(color2.slice(3, 5), 16);
    const b2 = parseInt(color2.slice(5, 7), 16);
    
    const r = Math.round(r1 + (r2 - r1) * t);
    const g = Math.round(g1 + (g2 - g1) * t);
    const b = Math.round(b1 + (b2 - b1) * t);
    
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
  };

  // Calculate min/max tensions for relative coloring
  const allTensions = relationships.flatMap(r => [
    r.tension?.sourceToTarget || 0,
    r.tension?.targetToSource || 0
  ]).filter(t => t > 0);
  const minTension = allTensions.length > 0 ? Math.min(...allTensions) : 0;
  const maxTensionValue = allTensions.length > 0 ? Math.max(...allTensions) : 0;

  // Calculate min/max risk scores for relative coloring
  const allRiskScores = relationships.map(r => r.riskScore || 0).filter(r => r > 0);
  const minRiskScore = allRiskScores.length > 0 ? Math.min(...allRiskScores) : 0;
  const maxRiskScore = allRiskScores.length > 0 ? Math.max(...allRiskScores) : 0;

  return (
    <div className="relationship-management-ui">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h3 style={{ margin: 0 }}>Relationships</h3>
        <button
          onClick={() => {
            if (showForm) resetForm();
            else setShowForm(true);
          }}
          style={{
            padding: '8px 16px',
            background: showForm ? '#ef4444' : '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
        >
          {showForm ? 'Cancel' : '+ Add Relationship'}
        </button>
      </div>

      {error && (
        <div style={{ padding: '12px', background: '#fee', border: '1px solid #fcc', borderRadius: '4px', marginBottom: '16px', color: '#c00' }}>
          {error}
        </div>
      )}

      {/* Relationship Creation/Edit Form */}
      {showForm && (
        <div ref={formRef} className="relationship-form" style={{
          border: '1px solid #d1d5db',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '16px',
          background: '#fafafa',
        }}>
          <h4 style={{ marginTop: 0 }}>{editingRelationshipId ? 'Edit Relationship' : 'Create Relationship'}</h4>
          
          {/* Source Entity */}
          <div className="form-field" style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500', fontSize: '14px' }}>
              Source Entity: <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <select
              value={formData.sourceEntityId}
              onChange={(e) => setFormData({ ...formData, sourceEntityId: e.target.value })}
              style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
            >
              <option value="">Select source entity...</option>
              {entities.map(entity => (
                <option key={entity.id} value={entity.id}>{entity.name}</option>
              ))}
            </select>
          </div>

          {/* Target Entity */}
          <div className="form-field" style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500', fontSize: '14px' }}>
              Target Entity: <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <select
              value={formData.targetEntityId}
              onChange={(e) => setFormData({ ...formData, targetEntityId: e.target.value })}
              style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
            >
              <option value="">Select target entity...</option>
              {entities.map(entity => (
                <option key={entity.id} value={entity.id}>{entity.name}</option>
              ))}
            </select>
          </div>

          {/* Relationship Types */}
          <div className="form-field" style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500', fontSize: '14px' }}>
              Relationship Types: <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <div style={{ 
              border: '1px solid #d1d5db', 
              borderRadius: '4px', 
              padding: '8px',
              maxHeight: '150px',
              overflowY: 'auto'
            }}>
              {RELATIONSHIP_TYPES.map(type => (
                <label 
                  key={type.value} 
                  style={{ 
                    display: 'block', 
                    marginBottom: '6px', 
                    fontSize: '14px', 
                    cursor: 'pointer',
                    userSelect: 'none'
                  }}
                >
                  <input
                    type="checkbox"
                    checked={formData.relationshipTypes.includes(type.value)}
                    onChange={(e) => {
                      const newTypes = e.target.checked
                        ? [...formData.relationshipTypes, type.value]
                        : formData.relationshipTypes.filter(t => t !== type.value);
                      setFormData({ ...formData, relationshipTypes: newTypes });
                    }}
                    style={{ marginRight: '8px' }}
                  />
                  {type.label}
                </label>
              ))}
            </div>
            {formData.relationshipTypes.length === 0 && (
              <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                Select at least one relationship type
              </div>
            )}
          </div>

          {/* Financial Impact */}
          <div className="form-field" style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500', fontSize: '14px' }}>
              Financial Impact (USD):
            </label>
            <input
              type="number"
              value={formData.financialImpact}
              onChange={(e) => setFormData({ ...formData, financialImpact: parseFloat(e.target.value) || 0 })}
              placeholder="e.g., 1000000"
              style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
            />
          </div>

          {/* Description */}
          <div className="form-field" style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500', fontSize: '14px' }}>
              Description:
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Describe the relationship between entities..."
              rows={3}
              style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px', fontFamily: 'inherit' }}
            />
          </div>

          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={handleSubmit}
              disabled={loading}
              style={{
                padding: '10px 20px',
                background: '#22c55e',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontWeight: '500',
              }}
            >
              {loading ? 'Saving...' : editingRelationshipId ? 'Update Relationship' : 'Create Relationship'}
            </button>
            <button
              onClick={resetForm}
              style={{
                padding: '10px 20px',
                background: '#6b7280',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Relationship List */}
      <div className="relationship-list">
        {loading && !showForm && (
          <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
            Loading relationships...
          </div>
        )}

        {!loading && relationships.length === 0 && (
          <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
            No relationships found
          </div>
        )}

        <div style={{ display: 'grid', gap: '12px' }}>
          {relationships.map(relationship => (
            <div key={relationship.id} style={{
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              padding: '12px',
              background: 'white',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '8px' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '14px', marginBottom: '4px' }}>
                    <strong>{getEntityName(relationship.sourceEntityId)}</strong>
                    <span style={{ margin: '0 8px', color: '#666' }}>→</span>
                    <strong>{getEntityName(relationship.targetEntityId)}</strong>
                  </div>
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                    {Array.isArray(relationship.relationshipTypes) 
                      ? relationship.relationshipTypes.map((type: string) => 
                          RELATIONSHIP_TYPES.find(t => t.value === type)?.label || type
                        ).join(', ')
                      : relationship.relationshipType || 'Unknown'
                    }
                  </div>
                  {relationship.financialImpact > 0 && (
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      Financial Impact: ${relationship.financialImpact.toLocaleString()}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '4px' }}>
                  <button
                    onClick={() => handleEdit(relationship)}
                    style={{
                      padding: '4px 12px',
                      background: '#3b82f6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px',
                    }}
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(relationship.id)}
                    style={{
                      padding: '4px 12px',
                      background: '#ef4444',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px',
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>

              {/* Risk Score, Max Tension, and Description Row */}
              <div style={{ display: 'flex', gap: '16px', marginBottom: '8px', padding: '12px', background: '#f9fafb', borderRadius: '8px' }}>
                {/* Left side: Risk Score and Max Tension */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', minWidth: '200px' }}>
                  {/* Risk Score */}
                  {relationship.riskScore !== undefined && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontWeight: '600', fontSize: '12px' }}>Risk Score:</span>
                      <span style={{ 
                        fontSize: '18px',
                        fontWeight: '700',
                        color: getRelativeTensionColor(relationship.riskScore, minRiskScore, maxRiskScore)
                      }}>
                        {relationship.riskScore.toFixed(2)}
                      </span>
                    </div>
                  )}
                  
                  {/* Max Tension */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontWeight: '600', fontSize: '12px' }}>Max Tension:</span>
                    <span style={{
                      fontSize: '18px',
                      color: getRelativeTensionColor(relationship.maxTension || 0, minTension, maxTensionValue),
                      fontWeight: '700',
                    }}>
                      {(relationship.maxTension || 0).toFixed(2)}
                    </span>
                  </div>
                </div>

                {/* Right side: Description */}
                {relationship.description && (
                  <div style={{ flex: 1, paddingLeft: '16px', borderLeft: '1px solid #d1d5db' }}>
                    <div style={{ fontWeight: '600', fontSize: '12px', marginBottom: '4px', color: '#374151' }}>Description:</div>
                    <div style={{ fontSize: '12px', color: '#666', lineHeight: '1.5' }}>
                      {relationship.description}
                    </div>
                  </div>
                )}
              </div>

              {/* Tension Display */}
              <div style={{ marginTop: '8px', padding: '12px', background: '#f9fafb', borderRadius: '8px', fontSize: '12px' }}>
                {/* Collapsible Directional Tensions */}
                <details style={{ paddingTop: '0' }}>
                  <summary style={{ cursor: 'pointer', fontWeight: '600', fontSize: '13px', color: '#374151', userSelect: 'none' }}>
                    Directional Tensions & Breakdown
                  </summary>
                  
                  <div style={{ marginTop: '12px' }}>
                    {/* Source to Target */}
                    <div style={{ marginBottom: '12px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                        <span style={{ fontWeight: '600' }}>{getEntityName(relationship.sourceEntityId)} → {getEntityName(relationship.targetEntityId)}</span>
                        <span style={{
                          fontSize: '16px',
                          fontWeight: '700',
                          color: getRelativeTensionColor(relationship.tension?.sourceToTarget || 0, minTension, maxTensionValue),
                        }}>
                          {(relationship.tension?.sourceToTarget || 0).toFixed(2)}
                        </span>
                      </div>
                      
                      {/* Tension Breakdown */}
                      {relationship.tensionBreakdown?.sourceToTarget && (
                        <div style={{ marginTop: '6px', paddingTop: '6px', borderTop: '1px solid #e5e7eb', fontSize: '11px' }}>
                          <div style={{ fontWeight: '600', marginBottom: '4px', color: '#666' }}>Breakdown:</div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
                            <span>Direct Relationship Frictions:</span>
                            <span style={{ fontWeight: '600' }}>+{relationship.tensionBreakdown.sourceToTarget.directRelationshipFrictions.toFixed(2)}</span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
                            <span>Upstream Entity Frictions:</span>
                            <span style={{ fontWeight: '600' }}>+{relationship.tensionBreakdown.sourceToTarget.upstreamEntityFrictions.toFixed(2)}</span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
                            <span>Propagated from Network:</span>
                            <span style={{ fontWeight: '600' }}>+{relationship.tensionBreakdown.sourceToTarget.propagatedFromNetwork.toFixed(2)}</span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px', paddingTop: '4px', borderTop: '1px solid #d1d5db', fontWeight: '600' }}>
                            <span>Total:</span>
                            <span>{relationship.tensionBreakdown.sourceToTarget.total.toFixed(2)}</span>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Target to Source */}
                    <div style={{ paddingTop: '12px', borderTop: '1px solid #d1d5db' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                        <span style={{ fontWeight: '600' }}>{getEntityName(relationship.targetEntityId)} → {getEntityName(relationship.sourceEntityId)}</span>
                        <span style={{
                          fontSize: '16px',
                          fontWeight: '700',
                          color: getRelativeTensionColor(relationship.tension?.targetToSource || 0, minTension, maxTensionValue),
                        }}>
                          {(relationship.tension?.targetToSource || 0).toFixed(2)}
                        </span>
                      </div>
                      
                      {/* Tension Breakdown */}
                      {relationship.tensionBreakdown?.targetToSource && (
                        <div style={{ marginTop: '6px', paddingTop: '6px', borderTop: '1px solid #e5e7eb', fontSize: '11px' }}>
                          <div style={{ fontWeight: '600', marginBottom: '4px', color: '#666' }}>Breakdown:</div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
                            <span>Downstream Entity Frictions:</span>
                            <span style={{ fontWeight: '600' }}>+{relationship.tensionBreakdown.targetToSource.downstreamEntityFrictions.toFixed(2)}</span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
                            <span>Propagated from Network:</span>
                            <span style={{ fontWeight: '600' }}>+{relationship.tensionBreakdown.targetToSource.propagatedFromNetwork.toFixed(2)}</span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px', paddingTop: '4px', borderTop: '1px solid #d1d5db', fontWeight: '600' }}>
                            <span>Total:</span>
                            <span>{relationship.tensionBreakdown.targetToSource.total.toFixed(2)}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </details>
              </div>

              {/* Entity Frictions Affecting This Relationship */}
              <ExpandableList
                frictionId={relationship.id}
                frictionType="entity_friction"
                items={entityFrictionsCache.get(relationship.id) || []}
                count={entityFrictionsCounts.get(relationship.id) || 0}
                isLoading={loadingEntityFrictions.has(relationship.id)}
                onExpand={() => fetchEntityFrictions(relationship.id)}
                onItemClick={handleNavigateToFriction}
                customLabel="Entity Frictions"
              />

              {/* Applied Relationship Frictions */}
              <ExpandableList
                frictionId={relationship.id}
                frictionType="relationship_friction"
                items={relationshipFrictionsCache.get(relationship.id) || []}
                count={relationshipFrictionsCounts.get(relationship.id) || 0}
                isLoading={loadingFrictions.has(relationship.id)}
                onExpand={() => fetchRelationshipFrictions(relationship.id)}
                onItemClick={handleNavigateToFriction}
                customLabel="Relationship Frictions"
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
